import type { NhlTiebreakerTeam } from '../types';
import type { TiebreakerStepFn, StepResult } from '../types';
import { RankMethod } from '../../enums/rank-method';
import { addExplanation } from '../helpers';

/**
 * NHL tiebreaker step 7: Goals Scored (GF).
 *
 * If all teams have zero goalsFor (season not started / data unavailable),
 * the step is skipped (no-break). Otherwise the team(s) with the most goals
 * scored win the step.
 */
export function createGoalsForStep(): TiebreakerStepFn<NhlTiebreakerTeam> {
  return (teams, ctx): StepResult<NhlTiebreakerTeam> => {
    const hasScores = teams.some(t => t.goalsFor > 0);
    if (!hasScores) {
      for (const t of teams) {
        addExplanation(ctx, t, 'Goals For: no score data — skipped.');
      }
      return { winners: [], remaining: teams };
    }

    interface Entry { team: NhlTiebreakerTeam; gf: number }
    const ranked: Entry[] = teams.map(t => ({ team: t, gf: t.goalsFor }));

    const maxGf = Math.max(...ranked.map(r => r.gf));
    const winners = ranked.filter(r => r.gf === maxGf);
    const losers = ranked.filter(r => r.gf < maxGf);

    if (winners.length === teams.length) {
      for (const r of ranked) {
        addExplanation(ctx, r.team, `Goals For: ${r.gf} — tied.`);
      }
      return { winners: [], remaining: teams };
    }

    for (const r of winners) {
      addExplanation(ctx, r.team, `Goals For: ${r.gf} — best.`);
    }
    for (const r of losers) {
      addExplanation(ctx, r.team, `Goals For: ${r.gf}.`);
    }

    return {
      winners: winners.map(r => r.team),
      remaining: losers.map(r => r.team),
      rankMethod: RankMethod.GoalsFor,
    };
  };
}

export const goalsForStep = createGoalsForStep();
